import i18next, { type i18n } from 'i18next';
import { getI18n, initReactI18next } from 'react-i18next';

import ruAcknowledgements from './locales/ru/acknowledgements.json' with { type: 'json' };
import ruAuth from './locales/ru/auth.json' with { type: 'json' };
import ruRoles from './locales/ru/roles.json' with { type: 'json' };
import ruSecurity from './locales/ru/security.json' with { type: 'json' };
import ruDashboard from './locales/ru/dashboard.json' with { type: 'json' };
import ruDocuments from './locales/ru/documents.json' with { type: 'json' };
import ruDrafts from './locales/ru/drafts.json' with { type: 'json' };
import ruFilter from './locales/ru/filter.json' with { type: 'json' };
import ruAi from './locales/ru/ai.json' with { type: 'json' };
import ruCommon from './locales/ru/common.json' with { type: 'json' };
import ruContracts from './locales/ru/contracts.json' with { type: 'json' };
import ruLlm from './locales/ru/llm.json' with { type: 'json' };
import ruErrors from './locales/ru/errors.json' with { type: 'json' };
import ruEmployee from './locales/ru/employee.json' with { type: 'json' };
import ruLetters from './locales/ru/letters.json' with { type: 'json' };
import ruReference from './locales/ru/reference.json' with { type: 'json' };
import ruMembers from './locales/ru/members.json' with { type: 'json' };
import ruOffboarding from './locales/ru/offboarding.json' with { type: 'json' };
import ruOwnership from './locales/ru/ownership.json' with { type: 'json' };
import ruReactivation from './locales/ru/reactivation.json' with { type: 'json' };
import ruNav from './locales/ru/nav.json' with { type: 'json' };
import ruPagination from './locales/ru/pagination.json' with { type: 'json' };
import ruPermissions from './locales/ru/permissions.json' with { type: 'json' };
import ruProfile from './locales/ru/profile.json' with { type: 'json' };
import ruTeams from './locales/ru/teams.json' with { type: 'json' };
import ruTemplates from './locales/ru/templates.json' with { type: 'json' };
import ruValidation from './locales/ru/validation.json' with { type: 'json' };

/**
 * The two the product ships, primary first.
 *
 * Russian leads because the product is a Russian corporate legal department's workplace: the acts,
 * the contracts and the correspondence it works on are in Russian, and so is everybody who reads
 * them. English is the second language, not the source one — which is the reverse of the usual
 * arrangement and the reason `fallbackLng` below is `ru` rather than `en`.
 *
 * The order is also the order of the switcher (`shared/ui/language-control`), which maps over this
 * array.
 */
export const LANGUAGES = ['ru', 'en'] as const;

export type Language = (typeof LANGUAGES)[number];

/**
 * `permission` и `permissions` — два namespace, и разница между ними не косметическая.
 *
 * `permissions` — экран персональных исключений: заголовки колонок, тексты формы, подписи
 * источников. `permission` — описание каждого ключа каталога прав словами, по одной записи на
 * ключ; его адрес задан не здесь, а в `PERMISSION_META[key].descriptionKey`
 * (`packages/shared/src/permissions/permissions.catalog.ts`), и именно поэтому namespace назван в
 * единственном числе. Класть описания внутрь `permissions` значило бы разойтись с каталогом —
 * а каталог по инварианту 2 один на сервер и клиент.
 *
 * `letters`, а не `correspondence`, при том что маршрут называется `/correspondence`. Namespace
 * назван по домену, а не по адресу: сущность — `Letter` (`docs/product/glossary.md`, §4), право —
 * `letter:*`, юнит по `rules/naming-and-structure.mdc` п. 11 — `units/letter`, и
 * `units/correspondence` там прямо назван запрещённым синонимом. Один и тот же домен под двумя
 * именами — это ровно то расхождение, которое правило и закрывает; расходится здесь адрес, а он
 * принадлежит навигации, а не домену.
 */
const RESOURCES = {
  ru: {
    acknowledgements: ruAcknowledgements,
    auth: ruAuth,
    dashboard: ruDashboard,
    documents: ruDocuments,
    drafts: ruDrafts,
    roles: ruRoles,
    ai: ruAi,
    common: ruCommon,
    contracts: ruContracts,
    letters: ruLetters,
    llm: ruLlm,
    errors: ruErrors,
    employee: ruEmployee,
    filter: ruFilter,
    members: ruMembers,
    offboarding: ruOffboarding,
    ownership: ruOwnership,
    reactivation: ruReactivation,
    nav: ruNav,
    pagination: ruPagination,
    permissions: ruPermissions,
    profile: ruProfile,
    reference: ruReference,
    security: ruSecurity,
    teams: ruTeams,
    templates: ruTemplates,
    validation: ruValidation,
  },
};

/**
 * Русские словари, которых первый экран не спрашивает: имена здесь, содержимое — своим чанком.
 *
 * Английский уехал целиком (`en-catalogue.constant.ts`) — его никто не выбирает, и вопрос был
 * только в весе. С русским так нельзя: он язык по умолчанию и `fallbackLng`, и ленивый ввоз **всего**
 * русского вернул бы вспышку непереведённых ключей, ради устранения которой STORY-019-01 и делалась.
 * Поэтому граница проходит не по языку, а по экрану: перечисленное ниже не встречается нигде, кроме
 * своего юнита и своей страницы, — проверено перебором всех строковых литералов вида `<ns>.<key>`
 * в `src/**` тем же выражением, которым это делает `test/i18n/catalogue-parity.test.ts`.
 *
 * | Пространство имён | gzip | Где спрашивается |
 * |---|---|---|
 * | `assistant` | 5,28 кБ | `pages/assistant`, `units/assistant` — маршрут `/assistant` |
 * | `permission` | 4,17 кБ | `widgets/role-matrix`, `widgets/user-permissions` — `/admin/roles`, `/admin/members/$userId` |
 * | `evaluation` | 3,27 кБ | `pages/evaluation`, `units/evaluation` — `/evaluation` |
 * | `acts` | 2,37 кБ | `pages/acts`, `units/act` — `/acts`, `/acts/$actId` |
 *
 * **Ключ, который отсюда нельзя брать, — `crumbKey` маршрута.** Крошку рисуют раньше экрана:
 * ожидающий матч попадает в `useMatches()` вместе со `staticData` уже через `defaultPendingMs`, и
 * `RouteAnnouncer` пишет `t(crumbKey)` в заголовок вкладки до того, как `loader` этого маршрута
 * закончил. Это измерено прогоном роутера, а не выведено; гейт —
 * `test/i18n/deferred-catalogue.test.ts`. Ровно поэтому `ai` (5,56 кБ) здесь нет: в нём живут
 * `ai.title` и `ai.externalCalls.title`, то есть крошки двух админских маршрутов.
 *
 * Список **отложенных** — решение, а не копия перечня пространств имён: полный перечень по-прежнему
 * выводится (`NAMESPACES` ниже), и гейт сверяет его с каталогом файлов на диске.
 */
const RU_DEFERRED = {
  acts: () => import('./locales/ru/acts.json'),
  assistant: () => import('./locales/ru/assistant.json'),
  evaluation: () => import('./locales/ru/evaluation.json'),
  permission: () => import('./locales/ru/permission.json'),
};

export type DeferredNamespace = keyof typeof RU_DEFERRED;

export const DEFERRED_NAMESPACES = Object.keys(RU_DEFERRED) as readonly DeferredNamespace[];

/**
 * The namespaces that exist — derived from what is bundled plus what is deferred, and the only place
 * the list is computed.
 *
 * Exported rather than kept local because a second consumer needs it: the test harness
 * (`test/setup/testing-library.setup.ts`) initialises its own `cimode` instance and has to register
 * the same namespaces. It used to type them out, and that copy drifted exactly the way this one did
 * — `employee`, `members`, `offboarding` and `teams` were missing from it, so under `cimode` those
 * keys resolved through `common` and every component under test rendered `common.teams.field.name`
 * where the product renders `teams.field.name`. Nothing failed: the assertions that touch those
 * screens match with unanchored regular expressions, which are true of the drifted key as well.
 *
 * **Отложенные входят сюда наравне с остальными, и это обязательно.** `ns` — то, что решает, есть ли
 * такое пространство имён вообще: незарегистрированный `acts` перестаёт быть пространством имён и
 * становится первым сегментом ключа в `common` — запрос статуса акта ушёл бы искать
 * `common.acts.status.draft`, и ушёл бы **до** того, как каталог приедет. Имя стоит здесь с самого
 * начала, содержимое приезжает позже.
 *
 * One derivation, two consumers, no list to keep in step;
 * `test/i18n/namespace-registration.test.ts` fails if a hand-written list appears again, and
 * `test/i18n/deferred-catalogue.test.ts` compares the derivation with the locale directory.
 */
export const NAMESPACES: readonly string[] = [...Object.keys(RESOURCES.ru), ...DEFERRED_NAMESPACES];

/**
 * The translation instance of this tab.
 *
 * **`nsSeparator: '.'` is the decision this whole layout rests on**, and it was verified rather than
 * assumed: i18next splits a key on the *first* separator, so `auth.login.title` resolves as
 * namespace `auth` and key `login.title`, and `keySeparator` then walks the nesting. That is what
 * lets a key read as one hierarchical path everywhere in the code — the form ADR-0019 prescribes —
 * while the files stay split per namespace, which is what makes lazy loading per route chunk
 * possible later.
 *
 * **A missing key renders as the key.** i18next's default, kept deliberately: it degrades to exactly
 * what the interface showed before this layer existed, so a gap is visible on the screen instead of
 * being an empty element. `test/i18n/catalogue-parity.test.ts` is what stops one reaching a user.
 *
 * **`en` falls back to `ru`, and that is not a translation policy.** The parity gate makes the
 * fallback unreachable for any key the interface asks for; it exists for the seconds between adding
 * a key and adding its English text, when the alternative is a raw key on the screen. It points at
 * Russian because Russian is the language every string of this product is written in first.
 */
export const createI18n = (language: Language = 'ru'): i18n => {
  const instance = i18next.createInstance();

  void instance.use(initReactI18next).init({
    lng: language,
    fallbackLng: 'ru',
    // Derived, never typed out. A hand-kept copy of these names is a list that drifts silently: a
    // namespace present in `RESOURCES` and missing here loads nothing, and every string of that
    // feature renders as its own key — on the screen, in both languages, with the parity gate still
    // green (it compares the JSON files with each other, not with this list). That drift happened
    // here once: `offboarding` was added to `RESOURCES` and to both catalogues while this list —
    // written out by hand until then — still ended at `employee`. Nothing red said so; it was
    // noticed by looking at what the dialog rendered. See `NAMESPACES` above for why the derivation
    // is exported rather than inlined here.
    ns: NAMESPACES,
    defaultNS: 'common',
    nsSeparator: '.',
    keySeparator: '.',
    resources: RESOURCES,
    interpolation: {
      // React escapes what it renders; escaping again turns « into &laquo; on the screen.
      escapeValue: false,
    },
  });

  return instance;
};

/**
 * Догрузить каталог языка, которого нет в первом экране.
 *
 * Русский идёт в бандл сразу — на нём написан продукт и он же `fallbackLng`. Английский лежит
 * отдельным чанком и приезжает только если его действительно попросили: приложение сегодня
 * английский не выбирает вовсе (`createI18n()` зовётся без аргумента), поэтому статический импорт
 * означал 27 кБ gzip в первом экране за язык, который никто не видит.
 *
 * Вызывать **до** `changeLanguage`, иначе i18next покажет русский по `fallbackLng` и промолчит.
 */
export const loadLanguage = async (instance: i18n, language: Language): Promise<void> => {
  if (language === 'ru' || instance.hasResourceBundle('en', 'common')) return;

  const { EN_RESOURCES } = await import('./en-catalogue.constant.js');

  for (const [namespace, bundle] of Object.entries(EN_RESOURCES)) {
    instance.addResourceBundle('en', namespace, bundle, true, true);
  }
};

/**
 * Догрузить русский словарь экрана, которого нет в первом экране.
 *
 * Зовётся из `loader` маршрута — и именно оттуда, а не из компонента: роутер ждёт `loader` до
 * отрисовки, поэтому экран появляется уже переведённым. Догрузка внутри дерева означала бы первый
 * кадр сырыми ключами с заменой заголовков на глазах — ровно то, что STORY-019-01 убирала для
 * английского.
 *
 * **Экземпляр берётся из `react-i18next`, а не из контекста роутера.** `getI18n()` возвращает тот
 * самый экземпляр, который поставил `initReactI18next` внутри `createI18n` — то есть переводящий
 * экземпляр этой вкладки, тот же, на который опирается `useTranslation` без провайдера
 * (`test/setup/testing-library.setup.ts` называет это свойство прямо). Альтернатива — протащить
 * `i18n` в контекст роутера — потребовала бы экземпляра в момент создания `router`, а он создаётся
 * модулем раньше, чем `main.tsx` выбирает язык.
 *
 * **Каталог грузится всегда русский, какой бы язык ни был выбран.** Русский — `fallbackLng` любой
 * локали, а язык меняется переключателем, не покидая экрана: ветка «если сейчас `en`, не грузить»
 * экономила бы пару килобайт на чанке, который и так вне первой загрузки, и возвращала бы сырые
 * ключи тому, кто переключился обратно.
 *
 * Отказ не глотается: упавший `import()` роняет `loader`, и роутер показывает границу ошибки с
 * повтором — это лучше, чем экран, молча набранный ключами.
 */
export const loadCatalogue = async (
  namespace: DeferredNamespace,
  instance: i18n = getI18n(),
): Promise<void> => {
  if (instance.hasResourceBundle('ru', namespace)) return;

  const { default: bundle } = await RU_DEFERRED[namespace]();

  instance.addResourceBundle('ru', namespace, bundle, true, true);
};
